import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Play, Pause } from "@phosphor-icons/react";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { FmnWatermark } from "../FmnWatermark";

const FADE = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } };

const LEA_IMG = "/brand/team/lea-salon.png";

export function LeaStory() {
    const { t, i18n } = useTranslation();
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);

    // Pick the locale-matched audio file when available, fall back to English.
    // Files live in /app/frontend/public/audio/lea-hero.{lang}.mp3
    const LANG_AUDIO = { es: "/audio/lea-hero.es.mp3", it: "/audio/lea-hero.it.mp3", el: "/audio/lea-hero.el.mp3", zh: "/audio/lea-hero.zh.mp3", de: "/audio/lea-hero.de.mp3", fr: "/audio/lea-hero.fr.mp3", ja: "/audio/lea-hero.ja.mp3", pt: "/audio/lea-hero.pt.mp3", tl: "/audio/lea-hero.tl.mp3", hi: "/audio/lea-hero.hi.mp3", ar: "/audio/lea-hero.ar.mp3" };
    const audioSrc = LANG_AUDIO[i18n.language] || "/audio/lea-hero.mp3";

    function toggle() {
        if (!audioRef.current) return;
        if (playing) audioRef.current.pause();
        else audioRef.current.play().catch(() => {});
    }

    return (
        <section className="relative py-32 px-6 rem-petal-bg overflow-hidden" data-testid="lea-section" id="story">
            <FmnWatermark position="right" size={720} opacity={0.05} rotate={-12} />
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                <motion.div {...FADE} className="lg:col-span-5 relative">
                    <div className="rounded-3xl overflow-hidden shadow-rem-card aspect-[4/5] bg-[#EDF0F8]">
                        <img src={LEA_IMG} alt="Lea Millen, master hairdresser and remembr founder" className="w-full h-full object-cover object-top" data-testid="lea-portrait" />
                    </div>
                    <div className="absolute -bottom-6 -right-6 hidden md:block"><ForgetMeNotIcon size={72} /></div>
                </motion.div>

                <motion.div {...FADE} transition={{ ...FADE.transition, delay: 0.15 }} className="lg:col-span-7">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-6">{t("lea_story.eyebrow")}</p>
                    <h2 className="font-serif text-4xl sm:text-5xl text-[#2D2B2A] leading-tight">
                        {t("lea_story.headline_part1")} <em className="italic text-[#4A5A95]">{t("lea_story.headline_emphasis")}</em>
                    </h2>

                    <div className="mt-6 inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-white/80 border border-[#8C9ECB]/40 hover:border-[#6B7DB8] transition-colors" data-testid="lea-audio-player">
                        <button
                            onClick={toggle}
                            className="w-9 h-9 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] flex items-center justify-center text-white transition-all"
                            data-testid="lea-audio-toggle"
                            aria-label={playing ? t("lea_story.audio_aria_pause") : t("lea_story.audio_aria_play")}
                        >
                            {playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
                        </button>
                        <span className="text-sm text-[#5C5A59] pr-2">
                            {playing ? t("lea_story.audio_speaking") : t("lea_story.audio_play")}
                        </span>
                        <div className="w-16 h-1 rounded-full bg-[#E5E1D8] overflow-hidden">
                            <div className="h-full bg-[#6B7DB8] transition-all" style={{ width: `${progress}%` }} />
                        </div>
                    </div>
                    <audio
                        key={audioSrc}
                        ref={audioRef}
                        src={audioSrc}
                        preload="metadata"
                        onPlay={() => setPlaying(true)}
                        onPause={() => setPlaying(false)}
                        onEnded={() => { setPlaying(false); setProgress(0); }}
                        onTimeUpdate={(e) => {
                            const t = e.currentTarget;
                            if (t.duration) setProgress((t.currentTime / t.duration) * 100);
                        }}
                        data-testid="lea-audio"
                    />

                    <div className="mt-8 space-y-5 text-[#5C5A59] text-lg leading-relaxed font-light max-w-prose">
                        <p>{t("lea_story.p1")}</p>
                        <p>{t("lea_story.p2")}</p>
                        <p>{t("lea_story.p2b")}</p>
                        <p className="text-[#2D2B2A] font-serif italic text-xl text-balance">{t("lea_story.p3_quote")}</p>
                        <p>{t("lea_story.p4")}</p>
                        <p>{t("lea_story.p4b")}</p>
                        <p>{t("lea_story.p4c")}</p>
                        <p className="text-[#2D2B2A] font-serif italic text-balance" data-testid="lea-p5">{t("lea_story.p5")}</p>
                        <p className="text-[#2D2B2A] font-serif italic text-balance">{t("lea_story.p6")}</p>
                        <p className="mt-6 pt-6 border-t border-[#E5E1D8] text-[#4A5A95]" data-testid="lea-giveback">{t("lea_story.p7")}</p>
                        <p className="font-serif italic text-xl text-[#2D2B2A] text-balance" data-testid="lea-closing">{t("lea_story.p8")}</p>
                    </div>
                    <p className="mt-10 text-sm text-[#8A8786]" data-testid="lea-signature">{t("lea_story.signature")}</p>
                </motion.div>
            </div>
        </section>
    );
}
